import React, { useState } from "react";
import { Loader2, X, AlertTriangle, Trash2 } from "lucide-react";
import useProject from "../../../hooks/useProject";
import useSettings from "../../../hooks/useSettings";

const RemoveAccessModal = ({ project, user, onClose }) => {
  const { isRemovingMember, removeMember } = useProject();
  const { loadProjectsWithAccess } = useSettings();
  const [error, setError] = useState("");

  const handleRemove = async () => {
    const result = await removeMember(project._id, user._id || user.id);
    if (result.meta.requestStatus === "fulfilled") {
      loadProjectsWithAccess();
      onClose();
      return;
    }
    setError(result.payload || "Failed to remove access.");
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 z-50 w-[520px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 size={18} color="#ef4444" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Remove Access</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-5 leading-relaxed">
          Are you sure you want to remove access for this resource from the
          project?
        </p>

        {/* User Card */}
        <div className="border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {user?.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-blue-600">
                {(user?.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {user?.name || "—"}
              </p>
              <p className="text-xs text-gray-400">{user?.email || "—"}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Role</p>
            <p className="text-sm font-medium text-gray-700 capitalize">
              {user?.role
                ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                : "Member"}
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
          <AlertTriangle
            size={16}
            color="#ef4444"
            className="flex-shrink-0 mt-0.5"
          />
          <p className="text-xs text-red-500 leading-relaxed">
            This resource will lose access to the project and will no longer be
            able to view or manage any project data.
          </p>
        </div>

        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="h-11 px-8 rounded-xl border border-red-400 text-red-500 text-sm font-medium hover:bg-red-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleRemove}
            disabled={isRemovingMember}
            className="h-11 px-8 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2 transition"
          >
            {isRemovingMember && <Loader2 size={14} className="animate-spin" />}
            Remove Access
          </button>
        </div>
      </div>
    </>
  );
};

export default RemoveAccessModal;
